import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { forkJoin, of } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import {
  SEARCH_OPS_TASK_TRANSITIONS,
  SEARCH_OPS_WORK_TYPES,
  SearchBacklogItemDto,
  SearchChecklistTemplateDto,
  SearchCycleDto,
  SearchPropertyListItemDto,
  SearchTaskDetailDto,
  SearchTaskListItemDto
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-backlog',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TextareaModule,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.backlog.title')"
      [description]="t('growth.searchops.backlog.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.backlog.help.title'"
        [descriptionKey]="'growth.searchops.backlog.help.description'"
        [bulletKeys]="[
          'growth.searchops.backlog.help.bullets.cycle',
          'growth.searchops.backlog.help.bullets.position',
          'growth.searchops.backlog.help.bullets.task'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.backlog.actions.workHome')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/coworkers']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.backlog.actions.coworkers')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/keywords']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.keywords')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.backlog.sections.filters') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.backlog.filterHelp.title'"
              [descriptionKey]="'growth.searchops.backlog.filterHelp.description'" />
          </div>
          <div class="filters">
            <ain-form-field [label]="t('growth.searchops.fields.property')">
              <p-select
                [options]="propertyOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="selectedPropertyId"
                (ngModelChange)="onPropertyChange($event)"
                appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.searchops.fields.position')">
              <p-select
                [options]="positionOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="selectedPosition"
                (ngModelChange)="onPositionChange($event)"
                [showClear]="true"
                appendTo="body" />
            </ain-form-field>
          </div>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.backlog.sections.cycles') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.backlog.cycleHelp.title'"
              [descriptionKey]="'growth.searchops.backlog.cycleHelp.description'" />
          </div>
          <div class="toolbar">
            <button pButton type="button" [label]="t('growth.searchops.backlog.actions.createWeek')" (click)="createThisWeek()"></button>
          </div>
          @if (cycles().length === 0) {
            <ain-state type="empty" [title]="t('growth.searchops.backlog.cycles.empty')" />
          } @else {
            <p-table [value]="cycles()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.searchops.fields.title') }}</th>
                  <th>{{ t('growth.searchops.fields.kind') }}</th>
                  <th>{{ t('growth.searchops.fields.status') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.title }}</td>
                  <td>{{ t('growth.searchops.cycleKind.' + row.kind) }}</td>
                  <td>{{ t('growth.searchops.cycleStatus.' + row.status) }}</td>
                  <td>
                    @if (row.status !== 'closed') {
                      <button pButton type="button" size="small" severity="secondary" [label]="t('growth.searchops.backlog.actions.closeCycle')" (click)="closeCycle(row)"></button>
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.backlog.sections.items') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.backlog.itemsHelp.title'"
              [descriptionKey]="'growth.searchops.backlog.itemsHelp.description'" />
          </div>
          <button pButton type="button" [label]="t('growth.searchops.backlog.actions.addItem')" (click)="openBacklogDialog()"></button>
          @if (backlog().length === 0) {
            <ain-state type="empty" [title]="t('growth.searchops.backlog.items.empty')" />
          } @else {
            <p-table [value]="backlog()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.searchops.fields.title') }}</th>
                  <th>{{ t('growth.searchops.fields.position') }}</th>
                  <th>{{ t('growth.searchops.fields.type') }}</th>
                  <th>{{ t('growth.searchops.fields.status') }}</th>
                  <th>{{ t('growth.searchops.fields.impact') }}</th>
                  <th>{{ t('growth.searchops.fields.effort') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.title }}</td>
                  <td>{{ t('growth.searchops.positions.' + row.positionCode) }}</td>
                  <td>{{ t('growth.searchops.workType.' + row.type) }}</td>
                  <td>{{ t('growth.searchops.workStatus.' + row.status) }}</td>
                  <td>{{ row.impact }}</td>
                  <td>{{ row.effort }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.backlog.sections.tasks') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.backlog.taskHelp.title'"
              [descriptionKey]="'growth.searchops.backlog.taskHelp.description'" />
          </div>
          <button pButton type="button" [label]="t('growth.searchops.backlog.actions.addTask')" (click)="openTaskDialog()"></button>
          @if (tasks().length === 0) {
            <ain-state type="empty" [title]="t('growth.searchops.backlog.tasks.empty')" />
          } @else {
            <p-table [value]="tasks()">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.searchops.fields.title') }}</th>
                  <th>{{ t('growth.searchops.fields.position') }}</th>
                  <th>{{ t('growth.searchops.fields.status') }}</th>
                  <th>{{ t('growth.searchops.fields.source') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.title }}</td>
                  <td>{{ t('growth.searchops.positions.' + row.positionCode) }}</td>
                  <td>{{ t('growth.searchops.workStatus.' + row.status) }}</td>
                  <td>{{ t('growth.searchops.workSource.' + row.source) }}</td>
                  <td class="actions">
                    @for (status of nextStatuses(row.status); track status) {
                      <button pButton type="button" size="small" severity="secondary" [label]="t('growth.searchops.workStatus.' + status)" (click)="transition(row, status)"></button>
                    }
                    <button pButton type="button" size="small" [label]="t('growth.searchops.backlog.actions.checklist')" (click)="openChecklist(row)"></button>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }
    </ain-page>

    <p-dialog
      [header]="t('growth.searchops.backlog.dialog.item')"
      [(visible)]="backlogDialogOpen"
      [modal]="true"
      [style]="{ width: '32rem' }"
      appendTo="body">
      <ain-form-field [label]="t('growth.searchops.fields.title')">
        <input pInputText [(ngModel)]="backlogModel.title" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.position')">
        <p-select [options]="positionOptions().slice(1)" optionLabel="label" optionValue="value" [(ngModel)]="backlogModel.positionCode" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.type')">
        <p-select [options]="typeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="backlogModel.type" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.impact')">
        <p-inputNumber [(ngModel)]="backlogModel.impact" [min]="1" [max]="5" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.effort')">
        <p-inputNumber [(ngModel)]="backlogModel.effort" [min]="1" [max]="5" />
      </ain-form-field>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="backlogDialogOpen = false"></button>
        <button pButton type="button" [label]="t('common.save')" (click)="saveBacklogItem()"></button>
      </ng-template>
    </p-dialog>

    <p-dialog
      [header]="t('growth.searchops.backlog.dialog.task')"
      [(visible)]="taskDialogOpen"
      [modal]="true"
      [style]="{ width: '32rem' }"
      appendTo="body">
      <ain-form-field [label]="t('growth.searchops.fields.title')">
        <input pInputText [(ngModel)]="taskModel.title" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.position')">
        <p-select [options]="positionOptions().slice(1)" optionLabel="label" optionValue="value" [(ngModel)]="taskModel.positionCode" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.type')">
        <p-select [options]="typeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="taskModel.type" appendTo="body" />
      </ain-form-field>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="taskDialogOpen = false"></button>
        <button pButton type="button" [label]="t('common.save')" (click)="saveTask()"></button>
      </ng-template>
    </p-dialog>

    <p-dialog
      [header]="t('growth.searchops.backlog.dialog.checklist')"
      [(visible)]="checklistDialogOpen"
      [modal]="true"
      [style]="{ width: '36rem' }"
      appendTo="body">
      @if (taskDetail(); as detail) {
        <p class="summary">{{ detail.task.title }} · {{ t('growth.searchops.workStatus.' + detail.task.status) }}</p>
        @if (activeTemplate(); as template) {
          @for (item of template.items; track item.key) {
            <label class="check-row">
              <p-checkbox [(ngModel)]="checklistChecks[item.key]" [binary]="true" />
              <span>{{ t('growth.searchops.checklist.item.' + item.key) }}</span>
            </label>
          }
        }
        <ain-form-field [label]="t('growth.searchops.fields.worklogMinutes')">
          <p-inputNumber [(ngModel)]="worklogMinutes" [min]="1" [max]="1440" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.searchops.fields.worklogNote')">
          <textarea pTextarea [(ngModel)]="worklogNote" rows="2"></textarea>
        </ain-form-field>
      }
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="checklistDialogOpen = false"></button>
        <button pButton type="button" [label]="t('growth.searchops.backlog.actions.saveChecklist')" (click)="saveChecklist()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 12px; }
    .card { background: var(--ain-color-surface, #fff); padding: 16px; border-radius: 12px; margin-bottom: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
    .section-title h3 { margin: 0; font-size: 1rem; }
    .filters { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .actions { display: flex; flex-wrap: wrap; gap: 6px; }
    .check-row { display: flex; align-items: center; gap: 8px; margin: 6px 0; }
    .summary { color: #64748b; }
  `]
})
export class GrowthSearchOpsBacklogComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly loading = signal(false);
  readonly error = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly cycles = signal<SearchCycleDto[]>([]);
  readonly backlog = signal<SearchBacklogItemDto[]>([]);
  readonly tasks = signal<SearchTaskListItemDto[]>([]);
  readonly templates = signal<SearchChecklistTemplateDto[]>([]);
  readonly taskDetail = signal<SearchTaskDetailDto | null>(null);

  selectedPropertyId: string | null = null;
  selectedPosition: string | null = null;
  backlogDialogOpen = false;
  taskDialogOpen = false;
  checklistDialogOpen = false;
  backlogModel = { title: '', positionCode: 'seo.onpage', type: 'onpage', impact: 3, effort: 3 };
  taskModel = { title: '', positionCode: 'seo.onpage', type: 'onpage' };
  checklistChecks: Record<string, boolean> = {};
  worklogMinutes = 30;
  worklogNote = '';
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthSearchOpsChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.selectedPosition = params.get('position');
      this.load(true);
    });
    this.api.listChecklists().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: items => this.templates.set(items)
    });
    void this.setupLiveSubscription();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  propertyQuery(): Record<string, string> {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  propertyOptions(): Array<{ label: string; value: string }> {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  positionOptions(): Array<{ label: string; value: string | null }> {
    return [
      { label: this.t('growth.searchops.backlog.allPositions'), value: null },
      ...['seo.lead', 'seo.technical', 'seo.onpage', 'seo.content', 'seo.offpage', 'seo.local', 'seo.sem', 'seo.analyst', 'seo.dev']
        .map(code => ({ label: this.t('growth.searchops.positions.' + code), value: code }))
    ];
  }

  typeOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_WORK_TYPES.map(type => ({ label: this.t('growth.searchops.workType.' + type), value: type }));
  }

  nextStatuses(status: string): string[] {
    return SEARCH_OPS_TASK_TRANSITIONS[status] ?? [];
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { propertyId }, queryParamsHandling: 'merge' });
  }

  onPositionChange(position: string | null): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { position: position || null },
      queryParamsHandling: 'merge'
    });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.listProperties().pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: properties => {
        this.properties.set(properties);
        if (!this.selectedPropertyId && properties.length > 0) {
          this.selectedPropertyId = properties[0].id;
        }
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  createThisWeek(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    const range = this.currentWeekRange();
    this.api.createCycle(this.selectedPropertyId, {
      kind: 'week',
      startAtUtc: range.startAtUtc,
      endAtUtc: range.endAtUtc,
      title: range.title,
      status: 'active'
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadBoard(),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  closeCycle(cycle: SearchCycleDto): void {
    this.api.closeCycle(cycle.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadBoard(),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  openBacklogDialog(): void {
    this.backlogModel = {
      title: '',
      positionCode: this.selectedPosition || 'seo.onpage',
      type: 'onpage',
      impact: 3,
      effort: 3
    };
    this.backlogDialogOpen = true;
  }

  saveBacklogItem(): void {
    if (!this.selectedPropertyId || !this.backlogModel.title.trim()) {
      return;
    }
    const activeCycle = this.cycles().find(item => item.status === 'active');
    this.api.createBacklogItem(this.selectedPropertyId, {
      cycleId: activeCycle?.id ?? null,
      positionCode: this.backlogModel.positionCode,
      type: this.backlogModel.type,
      impact: this.backlogModel.impact,
      effort: this.backlogModel.effort,
      title: this.backlogModel.title,
      status: 'ready'
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.backlogDialogOpen = false;
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  openTaskDialog(): void {
    this.taskModel = {
      title: '',
      positionCode: this.selectedPosition || 'seo.onpage',
      type: 'onpage'
    };
    this.taskDialogOpen = true;
  }

  saveTask(): void {
    if (!this.selectedPropertyId || !this.taskModel.title.trim()) {
      return;
    }
    const activeCycle = this.cycles().find(item => item.status === 'active');
    const checklistKey = this.taskModel.type === 'technical' ? 'technical' : this.taskModel.type === 'onpage' ? 'onpage' : null;
    this.api.createTask(this.selectedPropertyId, {
      cycleId: activeCycle?.id ?? null,
      title: this.taskModel.title,
      positionCode: this.taskModel.positionCode,
      type: this.taskModel.type,
      status: 'ready',
      checklistTemplateKey: checklistKey
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.taskDialogOpen = false;
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  transition(task: SearchTaskListItemDto, toStatus: string): void {
    this.api.transitionTask(task.id, toStatus).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => this.loadBoard(),
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  openChecklist(task: SearchTaskListItemDto): void {
    this.api.getTask(task.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: detail => {
        this.taskDetail.set(detail);
        const template = this.templateFor(detail.task);
        this.checklistChecks = {};
        if (template) {
          const checked = this.parseChecked(detail.checklist?.resultsJson);
          for (const item of template.items) {
            this.checklistChecks[item.key] = checked.has(item.key);
          }
        }
        this.worklogMinutes = 30;
        this.worklogNote = '';
        this.checklistDialogOpen = true;
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  activeTemplate(): SearchChecklistTemplateDto | null {
    const detail = this.taskDetail();
    return detail ? this.templateFor(detail.task) : null;
  }

  saveChecklist(): void {
    const detail = this.taskDetail();
    const template = this.activeTemplate();
    if (!detail || !template) {
      return;
    }
    const results = template.items.map(item => ({ key: item.key, checked: !!this.checklistChecks[item.key] }));
    this.api.upsertTaskChecklist(detail.task.id, template.key, JSON.stringify(results))
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          if (this.worklogMinutes > 0) {
            this.api.addWorklog(detail.task.id, this.worklogMinutes, this.worklogNote).pipe(
              takeUntilDestroyed(this.destroyRef)
            ).subscribe({ next: () => this.finishChecklist(), error: () => this.finishChecklist() });
          } else {
            this.finishChecklist();
          }
        },
        error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
      });
  }

  private finishChecklist(): void {
    this.checklistDialogOpen = false;
    this.loadBoard();
  }

  private templateFor(task: SearchTaskListItemDto): SearchChecklistTemplateDto | null {
    const key = task.checklistTemplateKey
      || (task.type === 'technical' ? 'technical' : task.type === 'onpage' ? 'onpage' : null);
    return this.templates().find(item => item.key === key) ?? null;
  }

  private parseChecked(resultsJson?: string | null): Set<string> {
    const keys = new Set<string>();
    if (!resultsJson) {
      return keys;
    }
    try {
      const parsed = JSON.parse(resultsJson) as Array<{ key?: string; checked?: boolean }>;
      for (const item of parsed) {
        if (item?.key && item.checked) {
          keys.add(item.key);
        }
      }
    } catch {
      return keys;
    }
    return keys;
  }

  private loadBoard(): void {
    if (!this.selectedPropertyId) {
      this.cycles.set([]);
      this.backlog.set([]);
      this.tasks.set([]);
      return;
    }
    const propertyId = this.selectedPropertyId;
    const filters = { position: this.selectedPosition };
    forkJoin({
      cycles: this.api.listCycles(propertyId).pipe(catchError(() => of([] as SearchCycleDto[]))),
      backlog: this.api.listBacklog(propertyId, filters).pipe(catchError(() => of([] as SearchBacklogItemDto[]))),
      tasks: this.api.listTasks(propertyId, filters).pipe(catchError(() => of([] as SearchTaskListItemDto[])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: board => {
        this.cycles.set(board.cycles);
        this.backlog.set(board.backlog);
        this.tasks.set(board.tasks);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private currentWeekRange(): { startAtUtc: string; endAtUtc: string; title: string } {
    const now = new Date();
    const utcDate = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const day = new Date(utcDate).getUTCDay();
    const diff = (day + 6) % 7;
    const monday = new Date(utcDate - diff * 24 * 60 * 60 * 1000);
    const end = new Date(monday.getTime() + 7 * 24 * 60 * 60 * 1000);
    const year = monday.getUTCFullYear();
    const oneJan = Date.UTC(year, 0, 1);
    const week = Math.floor(((monday.getTime() - oneJan) / 86400000 + new Date(oneJan).getUTCDay() + 6) / 7);
    return {
      startAtUtc: monday.toISOString(),
      endAtUtc: end.toISOString(),
      title: `Week ${year}-W${String(week).padStart(2, '0')}`
    };
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
